import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Keyboard, Pagination, Thumbs, Zoom } from "swiper/modules";
import type { Swiper } from "swiper/types";

import { ZoomInIcon } from "@radix-ui/react-icons";

import { ProductImage } from "~/components/product-list/product-image";
import { Button } from "~/components/ui/button";
import { Carousel } from "~/components/ui/carousel";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { usePick } from "~/lib/utils/screens";
import { getVariantImage } from "~/shop/utils/image";

export const ProductImages = ({
  product,
}: {
  product: SerializeFrom<C_ProductInfo>;
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>();
  const [activeLightBoxSlide, setActiveLightBoxSlide] = useState<number>(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
  const [searchParams] = useSearchParams();

  const pick = usePick();
  const slidesPerView = pick([4, 7, 7, 10, 10]) || 10;

  const imagesLarge = useMemo(() => {
    const imagesLarge =
      getVariantImage(
        product,
        Object.fromEntries(searchParams.entries()),
        "large",
      ) ?? [];
    return imagesLarge;
  }, [product, searchParams]);

  return (
    <div className="flex w-full flex-col items-start">
      {/* Image selector */}
      <div className="relative w-full">
        <Carousel
          spaceBetween={24}
          className="mx-0 h-auto w-full transition sm:rounded-xl"
          modules={[Pagination]}
          pagination={{
            bulletClass: "swiper-pagination-bullet",
            clickable: true,
          }}
          slidesPerView={imagesLarge.length > 1 ? 1.15 : 1}
          useBubblesNavigation={imagesLarge.length > 1}
          onSlideChange={({ activeIndex }) =>
            setActiveLightBoxSlide(activeIndex)
          }
        >
          {imagesLarge.map((image, i) => (
            <ProductImage key={i} image={image} className="h-[940px] w-full" />
          )) ?? []}
        </Carousel>
        <Button
          variant="outline"
          className="absolute bottom-5 right-5 z-10 h-10 w-10 rounded-full bg-page-background p-0 text-base text-text-dark-on-light shadow-md active:bg-panel-translucent disabled:border-0 disabled:bg-panel-translucent"
          onClick={() => setIsLightboxOpen(true)}
        >
          <ZoomInIcon className="h-5 w-5 origin-center object-none" />
        </Button>
      </div>
      {/* Image zoom */}
      <Dialog
        open={isLightboxOpen}
        onOpenChange={() => {
          setIsLightboxOpen(false);
          setThumbsSwiper(null);
        }}
      >
        <DialogContent className="fixed left-0 top-0 block h-full w-full max-w-none translate-x-0 translate-y-0 bg-page-background p-0 [&>button]:z-10">
          <div className="flex h-full w-full flex-col">
            <Carousel
              thumbs={{ swiper: thumbsSwiper }}
              keyboard={{
                enabled: true,
              }}
              pagination={{
                bulletClass: "swiper-pagination-bullet",
                clickable: true,
              }}
              useBubblesNavigation={imagesLarge.length > 1}
              initialSlide={activeLightBoxSlide}
              onSlideChange={({ activeIndex }) =>
                setActiveLightBoxSlide(activeIndex)
              }
              className="mx-0 flex-1"
              modules={[Pagination, Zoom, Keyboard, Thumbs]}
            >
              {imagesLarge.map((image, i) => (
                <ProductImage key={`zoom-image-${i}`} image={image} />
              )) ?? []}
            </Carousel>
            <div className="py-1">
              <Carousel
                spaceBetween={2}
                watchSlidesProgress
                onSwiper={s => setThumbsSwiper(s)}
                initialSlide={activeLightBoxSlide}
                keyboard={{
                  enabled: true,
                }}
                useBubblesNavigation={imagesLarge.length > slidesPerView}
                className="mx-0 [&>.swiper-wrapper]:flex [&>.swiper-wrapper]:items-center [&>.swiper-wrapper]:justify-center"
                modules={[Keyboard, Thumbs]}
                slidesPerView={slidesPerView}
                slideClassName="!mr-4"
              >
                {imagesLarge.map((image, i) => (
                  <ProductImage
                    className={cn(
                      activeLightBoxSlide === i &&
                        "border-accent-11 [&>img]:border-2",
                      "rounded-lg",
                    )}
                    key={`zoom-image-carousel${i}`}
                    image={image}
                  />
                )) ?? []}
              </Carousel>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
